import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import { Typography } from '@mui/material'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { useQuery } from '@tanstack/react-query'
import { useDropdownLocationSweepstakesStore } from 'stores/useDropdownLocationSweepstakesStore'
import { ErrorIndicator } from 'components/ErrorIndicator/ErrorIndicator'
import { type Sweepstake } from 'types/api'
import { useDropdownLocationSweepstakesParticipatingLocationsStore } from 'stores/useDropdownLocationSweepstakesParticipatingLocationStore'

interface SelectDropdownSweepstakesProps {
  liveOnly?: boolean
}

const findSweepstake = ({
  id,
  sweepstakes,
}: {
  id: number
  sweepstakes: Sweepstake[] | undefined
}) => {
  const sweepstake = sweepstakes?.find((x) => x.id === id)

  if (!sweepstake) {
    return <ErrorIndicator />
  }
  return sweepstake?.name ?? null // Return null if not found
}

export const DropdownLocationSweepstakes = ({
  liveOnly,
}: SelectDropdownSweepstakesProps) => {
  const ITEM_HEIGHT = 48
  const ITEM_PADDING_TOP = 8

  const setDropdownLocationSweepstakesValue =
    useDropdownLocationSweepstakesStore(
      (state) => state.setdropdownLocationSweepstakesValue
    )
  const setDropdownLocationSweepstakesParticipatingLocationsValue =
    useDropdownLocationSweepstakesParticipatingLocationsStore(
      (state) => state.setdropdownLocationSweepstakesParticipatingLocationsValue
    )

  const get = useTypedApiClient({
    path: '/admin/contests/type/{contestType}',
    method: 'get',
  })
  const { isError, data: contests } = useQuery({
    queryKey: ['location-sweepstakes'],
    queryFn: async () => {
      const { data } = await get({ contestType: 'LocationSweepstake' })

      return data
    },
    select: (data: any[]) => {
      let filteredData = data.filter((x) => x.sweepstakeData?.state !== 'Draft')

      filteredData = filteredData.sort((a, b) => a.name.localeCompare(b.name))

      // Loop through the filtered data and set the name to include a dash and a number for names that are the same
      // First, count occurrences of each name
      const nameCount: Record<string, number> = {}
      filteredData.forEach((item) => {
        if (nameCount[item.name]) {
          nameCount[item.name] += 1
        } else {
          nameCount[item.name] = 1
        }
      })

      // Now update the names with the index
      const nameIndex: Record<string, number> = {}
      filteredData = filteredData.map((item) => {
        if (nameCount[item.name] > 1) {
          nameIndex[item.name] = nameIndex[item.name]
            ? nameIndex[item.name] + 1
            : 1
          item.name = `${String(item.name)}-${nameIndex[item.name]}`
        }
        return item
      })

      // If liveOnly is true, filter to only include live sweepstakes
      // Do this check after modifying names to avoid affecting the name modification logic
      if (liveOnly) {
        filteredData = filteredData.filter(
          (x) => x.sweepstakeData?.state === 'Live'
        )
      }

      // Return the modified filtered data
      return filteredData
    },
  })

  if (isError) {
    return <Typography>Error</Typography>
  }

  const handleChange = (event: SelectChangeEvent) => {
    setDropdownLocationSweepstakesValue(event.target.value)
    setDropdownLocationSweepstakesParticipatingLocationsValue('')
  }

  return (
    <FormControl>
      <Select
        placeholder="Select Location Sweepstake"
        fullWidth
        displayEmpty
        input={<OutlinedInput />}
        variant="outlined"
        defaultValue=""
        inputProps={{ 'aria-label': 'Without label' }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 250,
            },
          },
        }}
        onChange={handleChange}
        renderValue={(selectedId) => {
          if (!selectedId) {
            return <em>Select Location Sweepstake</em>
          }

          const selectedSweepstake = findSweepstake({
            id: Number(selectedId),
            sweepstakes: contests,
          })

          return selectedSweepstake
        }}
      >
        {contests === undefined ? (
          <MenuItem disabled value="">
            <em>No Data</em>
          </MenuItem>
        ) : (
          contests.map((itm: any) => (
            <MenuItem key={itm.id} value={itm.id}>
              {itm.name}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  )
}
